import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import WhatsAppIcon from './icons/WhatsAppIcon';
import { useCMS } from '../store/cmsStore';

export default function Buses() {
  const { data } = useCMS();
  const [activeTab, setActiveTab] = useState(0);
  const [currentImage, setCurrentImage] = useState(0);

  const busTypes = data.buses.filter(bus => bus.active).sort((a, b) => a.order - b.order);

  if (busTypes.length === 0) {
    return null;
  }

  const currentBus = busTypes[activeTab] || busTypes[0];

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % currentBus.images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + currentBus.images.length) % currentBus.images.length);
  };

  return (
    <section id="buses" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-block bg-brown-100 text-brown-700 px-4 py-2 rounded-full text-sm font-cairo font-medium mb-4">
            أسطولنا
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-amiri font-bold text-gray-900 mb-4">
            باصاتنا الحديثة
          </h2>
          <p className="text-gray-600 font-cairo">
            موديل 2027 — اقتصادي وVIP — انطلاق من الرياض يومياً تقريباً
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-3 md:gap-4 mb-8 md:mb-12 overflow-x-auto pb-2">
          {busTypes.map((bus, index) => (
            <button
              key={bus.id}
              onClick={() => {
                setActiveTab(index);
                setCurrentImage(0);
              }}
              className={`px-6 md:px-8 py-3 md:py-4 rounded-xl font-cairo font-bold text-base md:text-lg transition-all flex-shrink-0 ${
                activeTab === index
                  ? 'bg-brown-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {bus.type}
            </button>
          ))}
        </div>

        {/* Image Gallery */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="relative">
            <img
              src={currentBus.images[currentImage]}
              alt={`باص ${currentBus.type}`}
              className="w-full h-64 md:h-96 object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400"><rect fill="%23f3f4f6" width="800" height="400"/><text fill="%239ca3af" x="400" y="200" dominant-baseline="middle" text-anchor="middle" font-size="24" font-family="Arial">صورة غير متوفرة</text></svg>';
              }}
            />
            
            {/* Navigation Arrows */}
            {currentBus.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition"
                >
                  <ChevronRight className="w-6 h-6 text-gray-800" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-800" />
                </button>
              </>
            )}

            {/* Badge */}
            <div className="absolute top-4 right-4 bg-brown-600 text-white px-4 py-2 rounded-full font-cairo font-bold text-sm">
              {currentBus.type}
            </div>

            {/* Dots */}
            {currentBus.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {currentBus.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition ${
                      currentImage === index ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl md:text-2xl font-amiri font-bold text-gray-900">
                  باص {currentBus.type}
                </h3>
                <p className="text-gray-600 font-cairo mt-2">{currentBus.seats}</p>
              </div>
              <a
                href={`https://wa.me/${data.settings.whatsapp}?text=السلام عليكم، أريد الاستفسار عن باص ${currentBus.type}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-xl font-cairo font-bold hover:bg-green-600 transition"
              >
                <WhatsAppIcon className="w-5 h-5" />
                احجز الآن
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}